<template>
  <div class="forms-list">
    <!-- Header -->
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">Forms</h4>
          <p class="text-muted mb-0">Manage your created forms</p>
        </div>
        <div>
          <b-button variant="primary" @click="createNewForm">
            <feather-icon icon="PlusIcon" size="16" class="mr-1" />
            Create New Form
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Search and Filters -->
    <b-card class="mb-2">
      <b-row>
        <b-col sm="6" md="6" class="mb-2">
          <b-form-group label="Search Forms" label-for="search-forms">
            <b-form-input
              id="search-forms"
              v-model="searchQuery"
              placeholder="Search by form title or description"
              @input="filterForms"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3" class="mb-2">
          <b-form-group label="Status" label-for="status-filter">
            <b-form-select
              id="status-filter"
              v-model="statusFilter"
              :options="statusOptions"
              @change="filterForms"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3" class="mb-2">
          <b-form-group label="Sort By" label-for="sort-by">
            <b-form-select
              id="sort-by"
              v-model="sortBy"
              :options="sortOptions"
              @change="filterForms"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Forms Grid -->
    <b-row>
      <b-col
        v-for="form in filteredForms"
        :key="form.id"
        sm="6"
        lg="4"
        md="6"
        class="mb-3"
      >
        <b-card class="form-card h-100">
          <div class="form-card-header d-flex justify-content-between align-items-start mb-3">
            <div class="form-title">
              <h5 class="mb-1">{{ form.title || 'Untitled Form' }}</h5>
              <p class="text-muted mb-0 small">{{ form.description || 'No description' }}</p>
            </div>
            <!-- Desktop: Dropdown Actions -->
            <div class="form-actions d-none d-md-block">
              <b-dropdown variant="outline-secondary" size="sm" right>
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" size="16" />
                </template>
                <b-dropdown-item @click="viewForm(form.id)">
                  <feather-icon icon="EyeIcon" size="14" class="mr-1" />
                  View
                </b-dropdown-item>
                <b-dropdown-item @click="editForm(form.id)">
                  <feather-icon icon="EditIcon" size="14" class="mr-1" />
                  Edit
                </b-dropdown-item>
                <b-dropdown-item @click="duplicateForm(form.id)">
                  <feather-icon icon="CopyIcon" size="14" class="mr-1" />
                  Duplicate
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item @click="deleteForm(form.id)" class="text-danger">
                  <feather-icon icon="Trash2Icon" size="14" class="mr-1" />
                  Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <!-- Mobile: Direct Action Buttons -->
            <div class="form-actions d-block d-md-none w-100 mt-2">
              <b-button size="sm" variant="primary" class="mr-1 mb-1 w-100" @click="viewForm(form.id)">
                <feather-icon icon="EyeIcon" size="14" class="mr-1" /> View
              </b-button>
              <b-button size="sm" variant="secondary" class="mr-1 mb-1 w-100" @click="editForm(form.id)">
                <feather-icon icon="EditIcon" size="14" class="mr-1" /> Edit
              </b-button>
              <b-button size="sm" variant="warning" class="mr-1 mb-1 w-100" @click="archiveForm(form.id)">
                <feather-icon icon="ArchiveIcon" size="14" class="mr-1" /> Archive
              </b-button>
              <b-button size="sm" variant="danger" class="mb-1 w-100" @click="deleteForm(form.id)">
                <feather-icon icon="Trash2Icon" size="14" class="mr-1" /> Delete
              </b-button>
            </div>
          </div>

          <div class="form-card-content">
            <div class="form-stats d-flex justify-content-between mb-3">
              <div class="stat-item">
                <small class="text-muted">Questions</small>
                <div class="stat-value">{{ getQuestionCount(form) }}</div>
              </div>
              <div class="stat-item">
                <small class="text-muted">Responses</small>
                <div class="stat-value">{{ form.responses_count || 0 }}</div>
              </div>
              <div class="stat-item">
                <small class="text-muted">Status</small>
                <div class="stat-value">
                  <b-badge :variant="getStatusVariant(form.status)">
                    {{ form.status || 'Draft' }}
                  </b-badge>
                </div>
              </div>
              <div class="stat-item">
                <small class="text-muted">Assigned To</small>
                <div class="stat-value">
                  <b-badge :variant="getAssignmentVariant(form.assigned_to)">
                    {{ getAssignmentText(form.assigned_to) }}
                  </b-badge>
                </div>
              </div>
            </div>

            <div class="form-meta">
              <small class="text-muted">
                Created: {{ formatDate(form.created_at) }}
              </small>
              <br>
              <small class="text-muted">
                Last modified: {{ formatDate(form.updated_at) }}
              </small>
            </div>
          </div>

          <div class="form-card-footer mt-3">
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-2"
              @click="viewForm(form.id)"
            >
              <feather-icon icon="EyeIcon" size="14" class="mr-1" />
              View
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="editForm(form.id)"
            >
              <feather-icon icon="EditIcon" size="14" class="mr-1" />
              Edit
            </b-button>
            <b-button
              variant="outline-warning"
              size="sm"
              @click="archiveForm(form.id)"
            >
              <feather-icon icon="ArchiveIcon" size="14" class="mr-1" />
              Archive
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Empty State -->
    <div v-if="filteredForms.length === 0" class="empty-state text-center py-5">
      <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
      <h5 class="text-muted">No forms found</h5>
      <p class="text-muted">
        {{ searchQuery || statusFilter !== 'all' ? 'Try adjusting your search or filters' : 'Create your first form to get started' }}
      </p>
      <b-button v-if="!searchQuery && statusFilter === 'all'" variant="primary" @click="createNewForm">
        <feather-icon icon="PlusIcon" size="16" class="mr-1" />
        Create Your First Form
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import FormsService from '@/services/forms-service'

export default {
  name: 'FormsList',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    FeatherIcon,
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      sortBy: 'createdAt',
      forms: [],
      statusOptions: [
        { value: 'all', text: 'All Status' },
        { value: 'draft', text: 'Draft' },
        { value: 'published', text: 'Published' },
        { value: 'archived', text: 'Archived' },
      ],
      sortOptions: [
        { value: 'createdAt', text: 'Date Created' },
        { value: 'updatedAt', text: 'Last Modified' },
        { value: 'title', text: 'Title' },
        { value: 'responses', text: 'Responses' },
      ],
    }
  },
  computed: {
    filteredForms() {
      let filtered = [...this.forms]

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(form => 
          form.title.toLowerCase().includes(query) ||
          form.description.toLowerCase().includes(query)
        )
      }

      // Status filter
      if (this.statusFilter !== 'all') {
        filtered = filtered.filter(form => 
          form.status.toLowerCase() === this.statusFilter
        )
      }

      // Sort
      filtered.sort((a, b) => {
        if (this.sortBy === 'title') {
          return a.title.localeCompare(b.title)
        } else if (this.sortBy === 'responses') {
          return (b.responses_count || 0) - (a.responses_count || 0)
        } else if (this.sortBy === 'updatedAt') {
          return new Date(b.updated_at) - new Date(a.updated_at)
        } else {
          return new Date(b.created_at) - new Date(a.created_at)
        }
      })

      return filtered
    },
  },
  methods: {
    getQuestionCount(form) {
      return form.questions && form.questions.length ? form.questions.length : 0
    },
    filterForms() {
      // This method is called when search or filters change
      // The computed property handles the filtering automatically
    },
    createNewForm() {
      this.$router.push({ name: 'tools-forms-create' })
    },
    viewForm(id) {
      this.$router.push({ name: 'tools-forms-view', params: { id } })
    },
    editForm(id) {
      this.$router.push({ name: 'tools-forms-edit', params: { id } })
    },
    async duplicateForm(id) {
      try {
        const response = await FormsService.duplicateForm(id)
        if (response.success && response.data) {
          this.forms.push(response.data)
        }
        
        this.$bvToast.toast('Form duplicated successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast('Error duplicating form', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    deleteForm(id) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this form?', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await FormsService.deleteForm(id)
            // Remove from local array
            this.forms = this.forms.filter(form => form.id !== id)
            
            this.$bvToast.toast('Form deleted successfully!', {
              title: 'Success',
              variant: 'success',
              solid: true,
            })
          } catch (error) {
            this.$bvToast.toast('Error deleting form', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            })
          }
        }
      })
    },
    getStatusVariant(status) {
      const variants = {
        draft: 'secondary',
        published: 'success',
        archived: 'warning',
      }
      return variants[status && status.toLowerCase()] || 'secondary'
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    getAssignmentVariant(assignedTo) {
      const variants = {
        'all': 'primary',
        'managers': 'info',
        'employees': 'warning',
        'specific': 'success',
      }
      return variants[assignedTo] || 'secondary'
    },
    getAssignmentText(assignedTo) {
      const texts = {
        'all': 'All Users',
        'managers': 'Managers Only',
        'employees': 'Employees Only',
        'specific': 'Specific Users',
      }
      return texts[assignedTo] || assignedTo
    },
    addFormToList(newForm) {
      this.forms.push(newForm)
    },
    async fetchForms() {
      try {
        const response = await FormsService.getAllForms()
        // Handle the new API response structure
        if (response.success && response.data && response.data.forms) {
          this.forms = response.data.forms
        } else {
          this.forms = []
        }
      } catch (error) {
        console.error('Error fetching forms:', error)
        this.forms = []
        this.$bvToast.toast('Error loading forms', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    updateFormsList() {
      this.fetchForms();
    },
    async archiveForm(formId) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to archive this form?', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'warning',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await FormsService.archiveForm(formId)
            // Update the form status in local array
            const formIndex = this.forms.findIndex(f => f.id === formId)
            if (formIndex !== -1) {
              this.forms[formIndex].status = 'archived'
            }
            this.$bvToast.toast('Form archived successfully!', {
              title: 'Success',
              variant: 'success',
              solid: true,
            });
          } catch (error) {
            this.$bvToast.toast('Error archiving form', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            })
          }
        }
      })
    },
  },
  async mounted() {
    await this.fetchForms();
  },
  async activated() {
    await this.fetchForms();
  }
}
</script>

<style lang="scss" scoped>
.forms-list {
  .form-card {
    border: 1px solid #e0e6ed;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      border-color: #7367f0;
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
      transform: translateY(-2px);
    }

    .form-card-header {
      .form-title {
        flex: 1;
        min-width: 0;

        h5 {
          color: #5e5873;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .form-card-content {
      .form-stats {
        .stat-item {
          text-align: center;

          .stat-value {
            font-weight: 600;
            color: #5e5873;
            margin-top: 2px;
          }
        }
      }

      .form-meta {
        border-top: 1px solid #e0e6ed;
        padding-top: 12px;
      }
    }

    .form-card-footer {
      border-top: 1px solid #e0e6ed;
      padding-top: 12px;
    }
  }

  .empty-state {
    color: #6e6b7b;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .forms-list {
    padding: 0.5rem;
  }
  
  .card {
    margin-bottom: 1rem;
  }
  
  .card-header {
    padding: 0.75rem;
  }
  
  .card-body {
    padding: 0.75rem;
  }
  
  .btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
  }
  
  .form-label {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .form-control {
    font-size: 0.9rem;
  }
  
  .form-card {
    .form-card-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .form-actions {
      margin-top: 0.5rem;
      align-self: flex-end;
    }
    
    .form-stats {
      flex-wrap: wrap;
      gap: 0.5rem;
      
      .stat-item {
        flex: 1;
        min-width: 80px;
      }
    }
    
    .form-card-footer {
      .btn {
        margin-bottom: 0.25rem;
        margin-right: 0.25rem;
      }
    }
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .d-flex.justify-content-between > div:last-child {
    margin-top: 0.5rem;
  }
}

@media (max-width: 576px) {
  .forms-list {
    padding: 0.25rem;
  }
  
  .card-header {
    padding: 0.5rem;
  }
  
  .card-body {
    padding: 0.5rem;
  }
  
  .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
  
  .form-label {
    font-size: 0.8rem;
  }
  
  .form-control {
    font-size: 0.8rem;
  }
  
  .form-card {
    .form-card-header {
      flex-direction: column;
    }
    
    .form-actions {
      margin-top: 0.5rem;
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
    
    .form-stats {
      flex-direction: column;
      gap: 0.5rem;
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
    
    .form-card-footer {
      .btn {
        width: 100%;
        margin-bottom: 0.5rem;
        margin-right: 0;
      }
    }
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
  }
  
  .d-flex.justify-content-between > div:last-child {
    margin-top: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between > div:last-child .btn {
    width: 100%;
    margin-bottom: 0.25rem;
  }
}
</style> 